import { progressOf, type Room, type RoomClaim } from '@dia/db';
import type { StayPhase } from './accessWindow';
import { cacheAge, type CacheAge, type Forecast, type WeatherKind } from './weatherCache';

/**
 * The one live line on each home tile, worked out from what is already on the
 * phone. Pure, so the rules are tested without a screen, and so a tile never
 * says something the data does not.
 */

export type ArrivalFace =
  /** The boat time is set and the guest has not yet landed. */
  | { kind: 'boat'; time: string }
  /** Before arrival with no boat time yet: tell them to open it once at home. */
  | { kind: 'open_once' }
  /** After departure, the tile quietly says thank you. */
  | { kind: 'thanks' }
  /** In the house, after arrival day: the tile is just the guide. */
  | { kind: 'plain' };

export function arrivalFace(
  phase: StayPhase,
  boatTime: string | null | undefined,
  daysToArrival: number,
): ArrivalFace {
  if (phase === 'departed' || phase === 'closed') return { kind: 'thanks' };
  const time = boatTime?.trim();
  if (phase === 'in_house') {
    // Arrival day still wants the boat time. The day after, it is history.
    return time && daysToArrival === 0 ? { kind: 'boat', time } : { kind: 'plain' };
  }
  return time ? { kind: 'boat', time } : { kind: 'open_once' };
}

export type WeatherFace =
  | { kind: 'none' }
  | {
      kind: 'reading';
      tempC: number | null;
      sky: WeatherKind;
      age: CacheAge;
      /** Only an old reading says its age on the tile; a fresh one needs no apology. */
      showAge: boolean;
    };

export function weatherFace(
  cached: { fetchedAt: number; forecast: Forecast } | null,
  now: number = Date.now(),
): WeatherFace {
  if (!cached) return { kind: 'none' };
  const age = cacheAge(cached.fetchedAt, now);
  return {
    kind: 'reading',
    tempC: cached.forecast.now.tempC,
    sky: cached.forecast.now.kind,
    age,
    showAge: age.stale,
  };
}

export type RoomsFace =
  /** Nothing known yet: the tile shows its fixed line only. */
  | { kind: 'unknown'; offline: boolean }
  | { kind: 'none_picked'; offline: boolean }
  | { kind: 'count'; used: number; total: number; offline: boolean };

/**
 * "5 of 8 rooms picked", "Pick your room", and, with no signal, the honest
 * note that picking needs one (a claim is never queued, see Rooms.tsx).
 */
export function roomsFace(input: {
  rooms: Room[] | null;
  claims: Array<Pick<RoomClaim, 'room_id'>> | null;
  online: boolean;
}): RoomsFace {
  const offline = !input.online;
  if (!input.rooms || !input.claims) return { kind: 'unknown', offline };
  const progress = progressOf(input.rooms, input.claims, 0);
  if (progress.rooms === 0) return { kind: 'unknown', offline };
  if (progress.roomsUsed === 0) return { kind: 'none_picked', offline };
  return { kind: 'count', used: progress.roomsUsed, total: progress.rooms, offline };
}

export type LockReasonKey =
  | 'guest.home.locked_before'
  | 'guest.home.locked_cancelled'
  | 'guest.home.locked_after';

/** Why a private tile or row is closed, in the words the old home list used. */
export function lockReasonKey(phase: StayPhase, cancelled: boolean): LockReasonKey {
  if (phase === 'planning') return 'guest.home.locked_before';
  if (cancelled) return 'guest.home.locked_cancelled';
  return 'guest.home.locked_after';
}
